import { z } from 'zod';

import { requestJson } from '../api/http';
import type { ReferenceKind } from '../review/schema';
import {
  aliasSchema,
  referenceCandidatePageSchema,
  referenceCapturePageSchema,
  type Alias,
  type ReferenceCandidatePage,
  type ReferenceCapture,
} from './schema';

export type ReferenceListKind = 'customers' | 'products';

/** `ReferenceKind` ('customer'/'product', the alias-body and namespace vocabulary) to the plural
 * URL segment `GET /references/{...}` actually uses. Kept as an explicit table rather than
 * string-templating an "s" onto the end, so a future irregular kind cannot silently mismatch. */
const LIST_PATH_BY_KIND: Readonly<Record<ReferenceKind, ReferenceListKind>> = {
  customer: 'customers',
  product: 'products',
};

/** `GET /references/{kind}?limit=1` - just the capture's staleness, for the note list's
 * stale-reference indicator (`docs/implementation/06-mobile-web.md` 6.3). Resolving a stale
 * capture is out of scope; the Windows agent republishes it on its own sync schedule. */
export function fetchReferenceCapture(kind: ReferenceListKind): Promise<ReferenceCapture | null> {
  return requestJson(`/references/${kind}?limit=1`, referenceCapturePageSchema).then(
    (page) => page.capture,
  );
}

export interface ReferenceCandidateQuery {
  readonly limit?: number;
  readonly cursor?: string | null;
  readonly search?: string;
  readonly includeInactive?: boolean;
}

/** `GET /references/{customers|products}` - the real, searchable official-value list the 6.5
 * reference picker resolves fields against. Unlike `fetchReferenceCapture`, this is what a
 * review screen actually offers the owner to choose from. */
export function fetchReferenceCandidates(
  kind: ReferenceKind,
  query: ReferenceCandidateQuery = {},
): Promise<ReferenceCandidatePage> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.cursor !== null && query.cursor !== undefined) {
    params.set('cursor', query.cursor);
  }
  if (query.search !== undefined && query.search !== '') {
    params.set('search', query.search);
  }
  if (query.includeInactive === true) {
    params.set('include_inactive', 'true');
  }
  const queryString = params.toString();
  return requestJson(
    `/references/${LIST_PATH_BY_KIND[kind]}${queryString !== '' ? `?${queryString}` : ''}`,
    referenceCandidatePageSchema,
  );
}

/** `GET /references/aliases?reference_value_id=...` - every non-rejected alias of one official
 * value, oldest first, including retired ones as history (`app/api/lists.py`'s `list_aliases`). */
export function fetchAliasesForValue(referenceValueId: string): Promise<readonly Alias[]> {
  return requestJson(
    `/references/aliases?reference_value_id=${encodeURIComponent(referenceValueId)}`,
    z.array(aliasSchema),
  );
}

export interface CreateAliasInput {
  readonly kind: ReferenceKind;
  readonly surfaceForm: string;
  readonly referenceValueId: string;
}

/**
 * `POST /references/aliases` - the owner confirms, in one step, that a literal reading is an
 * alias of an official value. `201` for a genuinely new mapping, `200` for re-confirming an
 * identical one (both resolve this promise the same way; the caller does not need to tell them
 * apart). Refusals the caller must classify by `ApiError.status`/`code`:
 * - `409 alias_conflict` - that text is already mapped to a different active value.
 * - `422 alias_invalid` - `referenceValueId` does not exist or is the wrong kind.
 */
export function createAlias(input: CreateAliasInput): Promise<Alias> {
  return requestJson('/references/aliases', aliasSchema, {
    method: 'POST',
    body: {
      kind: input.kind,
      surface_form: input.surfaceForm,
      reference_value_id: input.referenceValueId,
    },
  });
}

/** `POST /references/aliases/{id}/retire` - stops a confirmed alias from resolving; the row
 * stays as history (`retired_at` set). Retiring an already-retired alias is idempotent. */
export function retireAlias(aliasId: string): Promise<Alias> {
  return requestJson(`/references/aliases/${aliasId}/retire`, aliasSchema, { method: 'POST' });
}
